import { apiFetch } from '../services/cloudflareService';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import * as storageService from '../services/storageService';
import { Building2, ChevronLeft, ArrowRight, ShieldCheck, HeartPulse, Search, Building } from 'lucide-react';

const Login: React.FC = () => {
    const [workspace, setWorkspace] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    
    // Easter egg for Super Admin
    const [isAdminMode, setIsAdminMode] = useState(false);
    const [adminPin, setAdminPin] = useState('');
    
    const navigate = useNavigate();
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!isAdminMode) {
            inputRef.current?.focus();
        }
    }, [isAdminMode]);

    const handleResolveWorkspace = async (e: React.FormEvent) => {
        e.preventDefault();
        const cleanWorkspace = workspace.trim().toLowerCase();
        
        if (!cleanWorkspace) return;
        
        if (cleanWorkspace === 'admin') {
            setIsAdminMode(true);
            setWorkspace('');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const API_URL = (window as any).__CF_WORKER_URL__ || '';
            const res = await apiFetch(`${API_URL}/api/auth/resolve/${cleanWorkspace}`);
            const data = await res.json();

            if (data.success) {
                if (data.type === 'hospital') {
                    navigate(`/${data.data.handle}/staff`);
                } else if (data.type === 'group') {
                    // Navigate to future Group Login page
                    navigate(`/c/${data.data.handle}`);
                }
            } else {
                setError('El espacio de trabajo no existe. Verifica e intenta de nuevo.');
                setWorkspace('');
                inputRef.current?.focus();
            }
        } catch {
            setError('Error de conexión. Intenta de nuevo.');
        } finally {
            setLoading(false);
        }
    };

    // --- SUPER ADMIN LOGIC ---
    const handleAdminKey = (num: string) => {
        if (adminPin.length < 6 && !loading) {
            setAdminPin(prev => prev + num);
            setError('');
        }
    };

    const handleAdminClear = () => { if (!loading) setAdminPin(''); };
    const handleAdminDelete = () => { if (!loading) setAdminPin(prev => prev.slice(0, -1)); };

    const submitAdminPin = async (pin: string) => {
        setLoading(true);
        setError('');
        try {
            const API_URL = (window as any).__CF_WORKER_URL__ || '';
            const res = await apiFetch(`${API_URL}/api/auth/login-by-pin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ pin }),
            });
            const data = await res.json();
            if (data.success && data.role === 'SUPER_ADMIN') {
                navigate('/admin/dashboard');
            } else {
                setError('PIN Maestro incorrecto.');
                setAdminPin('');
            }
        } catch {
            setError('Error de conexión.');
            setAdminPin('');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAdminMode && adminPin.length === 6) {
            submitAdminPin(adminPin);
        }
    }, [adminPin, isAdminMode]);

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col font-sans selection:bg-indigo-100 relative overflow-hidden">
            {/* Fondo decorativo (Vector abstract) */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none -z-10">
                <div className="absolute -top-[20%] -left-[10%] w-[70%] h-[70%] rounded-full bg-gradient-to-br from-indigo-100 to-indigo-50 blur-3xl opacity-70 animate-[pulse_8s_ease-in-out_infinite] pointer-events-none" />
                <div className="absolute -bottom-[20%] -right-[10%] w-[60%] h-[60%] rounded-full bg-gradient-to-tl from-emerald-100 to-indigo-50 blur-3xl opacity-60 animate-[pulse_10s_ease-in-out_infinite_reverse] pointer-events-none" />
            </div>

            <header className="p-8 relative z-20 flex justify-between items-center">
                <Link to="/" className="text-slate-500 hover:text-indigo-600 transition-all flex items-center gap-2 font-bold text-sm bg-white/50 backdrop-blur-md py-2 px-4 rounded-full border border-slate-200/50 hover:shadow-sm">
                    <ChevronLeft className="w-4 h-4" />
                    <span>Volver al Inicio</span>
                </Link>
                <div className="flex items-center gap-2 text-indigo-900/40">
                    <Building2 className="w-5 h-5" />
                    <span className="font-bold text-xs tracking-widest uppercase">Portal Global</span>
                </div>
            </header>

            <main className="flex-1 flex flex-col items-center justify-center px-6 -mt-12 relative z-10 w-full h-full">
                <div className={`relative z-50 w-full max-w-[400px] bg-white/70 backdrop-blur-xl rounded-[2.5rem] shadow-[0_30px_60px_-15px_rgba(0,0,0,0.05),0_0_0_1px_rgba(255,255,255,0.5)_inset] border border-white/40 p-10 text-center transition-all duration-700 ${isAdminMode ? 'animate-in fade-in slide-in-from-bottom-8' : 'animate-in fade-in zoom-in-95'}`}>
                    
                    {!isAdminMode ? (
                        <>
                            <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 w-16 h-16 rounded-[1.2rem] flex items-center justify-center mx-auto mb-8 shadow-lg shadow-indigo-600/20 relative group overflow-hidden">
                                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-500 rounded-[1.2rem]"></div>
                                <Building className={`w-7 h-7 text-white transition-all duration-500 relative z-10 ${loading ? 'animate-pulse' : 'group-hover:scale-110'}`} />
                                {loading && (
                                    <div className="absolute inset-0 rounded-[1.2rem] border-2 border-white/20 border-t-white animate-spin z-20" />
                                )}
                            </div>

                            <h1 className="text-2xl font-black text-slate-800 mb-2 tracking-tight">
                                Identifica tu Clínica
                            </h1>
                            <p className="text-slate-500 font-medium text-sm mb-8 tracking-wide">
                                Ingresa el Workspace asociado a tu Cadena o Sucursal.
                            </p>

                            <div className={`h-6 mb-2 transition-all duration-300 ${error ? 'opacity-100 transform-none' : 'opacity-0 translate-y-2'}`}>
                                {error && (
                                    <p className="text-red-500 text-[10px] font-bold bg-red-50/80 inline-block px-3 py-1 rounded-full border border-red-100">
                                        {error}
                                    </p>
                                )}
                            </div>

                            <form onSubmit={handleResolveWorkspace} className="flex gap-2 relative z-20">
                                <div className="relative flex-1 z-30">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <Search className="h-5 w-5 text-slate-400" />
                                    </div>
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        value={workspace}
                                        onChange={(e) => setWorkspace(e.target.value.toLowerCase())}
                                        disabled={loading}
                                        placeholder="ej. muguerza-sur"
                                        className="block w-full pl-11 pr-4 py-4 md:py-4 bg-white/80 border border-slate-200 rounded-2xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all font-semibold font-mono shadow-inner shadow-slate-100/50 disabled:opacity-50"
                                        autoComplete="off"
                                        autoCorrect="off"
                                        autoCapitalize="none"
                                        spellCheck="false"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading || !workspace.trim()}
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white p-4 rounded-2xl shadow-lg shadow-indigo-600/30 transition-all disabled:opacity-50 disabled:shadow-none hover:scale-105 active:scale-95 flex items-center justify-center flex-shrink-0"
                                >
                                    <ArrowRight className="w-5 h-5" />
                                </button>
                            </form>
                        </>
                    ) : (
                        <>
                            <div className="bg-gradient-to-br from-slate-800 to-slate-900 w-16 h-16 rounded-[1.2rem] flex items-center justify-center mx-auto mb-8 shadow-lg shadow-slate-900/20 relative">
                                <ShieldCheck className={`w-7 h-7 text-white transition-all duration-500 ${loading ? 'animate-pulse' : ''}`} />
                            </div>

                            <h1 className="text-2xl font-black text-slate-800 mb-2 tracking-tight">
                                Acceso Global
                            </h1>
                            <p className="text-slate-500 font-medium text-sm mb-6 tracking-wide">
                                Ingresa el PIN Maestro del ecosistema
                            </p>

                            <div className={`h-6 mb-2 transition-all duration-300 ${error ? 'opacity-100 transform-none' : 'opacity-0 translate-y-2'}`}>
                                {error && (
                                    <p className="text-red-500 text-xs font-bold bg-red-50/80 inline-block px-3 py-1 rounded-full border border-red-100">
                                        {error}
                                    </p>
                                )}
                            </div>

                            {/* PIN Viewer */}
                            <div className="flex justify-center gap-3 mb-8">
                                {[0, 1, 2, 3, 4, 5].map((i) => {
                                    const isFilled = adminPin.length > i;
                                    const isCurrent = adminPin.length === i;
                                    return (
                                        <div
                                            key={i}
                                            className={`w-10 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${error
                                                    ? 'border-red-500 bg-red-50/50 text-red-500'
                                                    : isFilled
                                                        ? 'bg-slate-800 border-slate-800 scale-105 shadow-md shadow-slate-800/20'
                                                        : isCurrent
                                                            ? 'bg-white/80 border-slate-300 border-2'
                                                            : 'bg-white/40 border-slate-200 border'
                                                }`}
                                        >
                                            {isFilled && <div className="w-2.5 h-2.5 bg-white rounded-full animate-in zoom-in duration-200" />}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Numpad */}
                            <div className="grid grid-cols-3 gap-3 max-w-[260px] mx-auto">
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                                    <button
                                        key={num}
                                        type="button"
                                        onClick={() => handleAdminKey(num.toString())}
                                        disabled={loading}
                                        className="w-full aspect-square rounded-2xl bg-white/60 hover:bg-white text-slate-800 font-black text-2xl transition-all shadow-sm border border-slate-100 active:scale-[0.92] disabled:opacity-40"
                                    >
                                        {num}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    onClick={handleAdminClear}
                                    disabled={loading || adminPin.length === 0}
                                    className="w-full aspect-square rounded-2xl flex items-center justify-center text-slate-400 font-bold hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
                                >
                                    C
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleAdminKey('0')}
                                    disabled={loading}
                                    className="w-full aspect-square rounded-2xl bg-white/60 hover:bg-white text-slate-800 font-black text-2xl transition-all shadow-sm border border-slate-100 active:scale-[0.92] disabled:opacity-40"
                                >
                                    0
                                </button>
                                <button
                                    type="button"
                                    onClick={handleAdminDelete}
                                    disabled={loading || adminPin.length === 0}
                                    className="w-full aspect-square rounded-2xl flex items-center justify-center text-slate-400 font-bold hover:text-slate-800 hover:bg-slate-50 transition-colors disabled:opacity-40"
                                >
                                    ⌫
                                </button>
                            </div>
                            
                            <button 
                                onClick={() => { setIsAdminMode(false); setAdminPin(''); setError(''); }}
                                className="mt-6 text-xs text-slate-400 hover:text-slate-600 transition-colors font-medium border-b border-transparent hover:border-slate-300 pb-0.5"
                            >
                                Cancelar
                            </button>
                        </>
                    )}
                </div>

                <div className="mt-12 flex items-center justify-center gap-3 text-slate-400/80 font-bold text-[10px] uppercase tracking-[0.2em]">
                    <HeartPulse className="w-4 h-4 text-indigo-300" />
                    <span>V.3.6.9</span>
                </div>
            </main>
        </div>
    );
};

export default Login;

